import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NbCardModule, NbDialogService, NbIconModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux';
import { Campaign } from 'src/app/models/campaign';
import { Insight } from 'src/app/models/insight';
import { ACTIONS } from 'src/app/shared/constants/general-constants';
import { ENVIRONMENT } from 'src/environments/environment';
import { WizardModalComponent } from './quick-start-wizard-modal/wizard-modal.component';
import { SharedModule } from '../../shared/shared.module';
import { StatsCampaignComponent } from '../campaigns/campaign-table/stats-campaign/stats-campaign.component';
import { ViewCampaignComponent } from '../campaigns/campaign-table/view-campaign/view-campaign.component';
import { CampaignState, LoadCampaigns, ReloadCampaign } from '../campaigns/redux';
import { ContentState } from '../content/redux';
import { LoadContent } from "../content/redux/content.actions";
import { StatsInsightComponent } from '../insights/insights-table/stats-insight/stats-insight.component';
import { ViewInsightComponent } from '../insights/insights-table/view-insight/view-insight.component';
import { InsightsState, LoadInsights, LoadInsightsInactive } from '../insights/redux';
import { LoadSurveys, SurveysState } from '../surveys/redux';

@Component({
  selector: 'app-quick-start',
  templateUrl: './quick-start.component.html',
  styleUrls: ['./quick-start.component.css'],
  imports: [NgIf, NbCardModule, NbIconModule, SharedModule]
})
export class QuickStartComponent implements OnInit, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  public activeInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsActive);
  public inactiveInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsInactive);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public username;
  public tableData;
  public insights = [];
  public showActions = [];
  public favorites;
  public isProduction: boolean = ENVIRONMENT.isProduction;

  constructor(
    private store: Store,
    private dialogService: NbDialogService
  ) {
    if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
      this.store.dispatch(LoadInsights);
    }
    if (!this.store.selectSnapshot(InsightsState.insightsInactive)) {
      this.store.dispatch(LoadInsightsInactive);
    }
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    if (!this.store.selectSnapshot(SurveysState.surveys)) {
      this.store.dispatch(LoadSurveys);
    }
    if (!this.store.selectSnapshot(CampaignState.campaign)) {
      this.store.dispatch(LoadCampaigns);
    }
  }

  public ngOnInit() {
    this.favorites = JSON.parse(localStorage.getItem('campaign_favorites')) || [];
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.activeInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights.concat(data);
      }
    });
    this.inactiveInsight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.insights = this.insights.concat(data);
      }
    });
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.tableData = data
          .filter((item) => item.created_by === this.username || this.favorites.includes(item.id))
          .map((item) => ({
            ...item,
            insight: this.insights.find(({ id }) => id === item.trigger_id)
          }));
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public openModal(type: string) {
    this.dialogService.open(WizardModalComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        type
      }
    });
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadCampaign);
    }
    if ($event.action === ACTIONS.VIEW) {
      const dataType = $event.dataType;
      if (dataType === 'campaign') {
        const campaign: Campaign = $event.data;
        this.dialogService.open(ViewCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            campaign
          }
        });
      }
      if (dataType === 'insight') {
        const insight: Insight = $event.data;
        this.dialogService.open(ViewInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            insight
          }
        });
      }
    }
    if ($event.action === ACTIONS.STATS) {
      const dataType = $event.dataType;
      if (dataType === 'campaign') {
        const campaign: Campaign = $event.data;
        this.dialogService.open(StatsCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            campaign
          }
        });
      }
      if (dataType === 'insight') {
        const insight: Insight = $event.data;
        this.dialogService.open(StatsInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            insight
          }
        });
      }
    }
  }
}
